import { Phone, Navigation, Mail, CalendarDays, MapPin, LayoutDashboard } from "lucide-react";
import { SectionHeading } from "./SectionHeading";
import { Reveal, Img } from "./Reveal";
import { AppointmentForm } from "./Forms";
import { BRAND, IMG } from "@/data/content";
import { useBooking } from "@/lib/booking";

const PHOTOS = [
  { src: IMG.clinicChair, alt: "Dental treatment room" },
  { src: IMG.clinicDentist, alt: "Doctor consultation" },
  { src: IMG.clinicCorridor, alt: "Clinic interior" },
];

export function FeaturedClinic() {
  const { openBooking } = useBooking();
  return (
    <section id="clinic" data-testid="featured-clinic-section" className="py-24 lg:py-32 bg-cream/60 relative overflow-hidden">
      <div className="pointer-events-none absolute -bottom-40 -right-40 h-[30rem] w-[30rem] rounded-full bg-teal-brand/10 blur-3xl" />
      <div className="relative max-w-7xl mx-auto px-5 md:px-8 lg:px-12">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <SectionHeading
            chapter="02"
            overline="Featured Clinic"
            title="Dyntra Clinics — Sector 75, Noida"
            sub="Your Local Destination for Dental & Skin Care — a modern, patient-friendly clinic environment with dedicated treatment rooms and contemporary dental equipment."
          />
          <Reveal delay={0.2}>
            <div className="flex items-center gap-4 rounded-2xl bg-white border border-pine/10 shadow-soft px-5 py-4 shrink-0">
              <Img src={IMG.doctorLead} alt="Dr. Ankita Pandey" className="h-14 w-14 rounded-full object-cover object-[center_15%] ring-2 ring-gold/50" />
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-gold">Clinic Owner</p>
                <p className="font-display text-lg font-semibold text-pine-deep">Dr. Ankita Pandey</p>
              </div>
            </div>
          </Reveal>
        </div>

        <div className="mt-12 grid grid-cols-3 gap-4">
          {PHOTOS.map((p, i) => (
            <Reveal key={p.alt} delay={i * 0.1}>
              <div className={`overflow-hidden rounded-2xl shadow-soft ${i === 0 ? "col-span-2" : ""}`}>
                <Img src={p.src} alt={p.alt} className={`w-full object-cover transition-transform duration-700 hover:scale-105 ${i === 0 ? "h-56 md:h-72" : "h-56 md:h-72"}`} />
              </div>
            </Reveal>
          ))}
        </div>

        <div className="mt-12 grid lg:grid-cols-5 gap-8 items-start">
          <Reveal className="lg:col-span-2">
            <div className="rounded-3xl bg-pine-deep text-ivory p-8 shadow-grand relative overflow-hidden grain">
              <p className="text-[11px] uppercase tracking-[0.25em] font-bold text-gold">Visit Us</p>
              <h3 className="mt-3 font-display text-2xl font-semibold">Book Your Consultation</h3>
              <p className="mt-3 text-sm text-ivory/70 leading-relaxed">
                Walk into our Sector 75 clinic or reach out — our team will find a time that works for you.
              </p>
              <div className="mt-6 space-y-3 text-sm">
                <p className="flex items-start gap-2.5 text-ivory/85"><MapPin className="mt-0.5 h-4 w-4 text-gold shrink-0" /> {BRAND.address}</p>
                <p className="flex items-center gap-2.5 text-ivory/85"><Phone className="h-4 w-4 text-gold shrink-0" /> {BRAND.phone}</p>
                <p className="flex items-center gap-2.5 text-ivory/85"><Mail className="h-4 w-4 text-gold shrink-0" /> {BRAND.email}</p>
              </div>
              <div className="mt-8 grid grid-cols-2 gap-3">
                <button data-testid="clinic-book-cta" onClick={() => openBooking()} className="inline-flex items-center justify-center gap-2 rounded-full bg-gold px-4 py-3 text-xs font-bold text-pine-deep transition-all duration-300 hover:bg-gold-soft">
                  <CalendarDays className="h-4 w-4" /> Book
                </button>
                <a data-testid="clinic-directions-cta" href={BRAND.mapsLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 rounded-full border border-ivory/25 px-4 py-3 text-xs font-bold text-ivory transition-all duration-300 hover:bg-ivory/10">
                  <Navigation className="h-4 w-4" /> Directions
                </a>
                <a data-testid="clinic-call-cta" href={BRAND.phoneLink} className="inline-flex items-center justify-center gap-2 rounded-full border border-ivory/25 px-4 py-3 text-xs font-bold text-ivory transition-all duration-300 hover:bg-ivory/10">
                  <Phone className="h-4 w-4" /> Call Clinic
                </a>
                <button data-testid="clinic-inquiry-cta" onClick={() => document.getElementById("clinic-form-card")?.scrollIntoView({ behavior: "smooth" })} className="inline-flex items-center justify-center gap-2 rounded-full border border-ivory/25 px-4 py-3 text-xs font-bold text-ivory transition-all duration-300 hover:bg-ivory/10">
                  <Mail className="h-4 w-4" /> Inquiry
                </button>
              </div>
              <a
                data-testid="clinic-manage-cta"
                href="/connect"
                className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-full border border-gold/50 bg-gold/10 px-4 py-3 text-xs font-bold text-gold transition-all duration-300 hover:bg-gold/20"
              >
                <LayoutDashboard className="h-4 w-4" /> Manage My Clinic — Dyntra Connect
              </a>
            </div>
          </Reveal>
          <Reveal delay={0.12} className="lg:col-span-3">
            <div id="clinic-form-card" className="rounded-3xl bg-white border border-pine/10 shadow-lift p-7 md:p-9">
              <AppointmentForm testidPrefix="clinic-appointment-form" />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
